import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Star, ShoppingBag, Heart, Store,
  Package, Pill, Apple, Cookie,
  Plus, Minus, Check, ExternalLink,
  Flag, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import {
  collection, addDoc, getDocs, query, orderBy,
  serverTimestamp, doc, updateDoc, getDoc,
} from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { useGroceryLists } from '../../hooks/useGroceryLists';
import { useGuestList } from '../../hooks/useGuestList';
import { useStores } from '../../hooks/useStores';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const REPORT_REASONS = [
  'Spam',
  'Inappropriate content',
  'Inaccurate information',
  'Other',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const safeString = (val) => {
  if (typeof val === 'string') return val;
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return val.name || val.text || val.value || '';
  return String(val);
};

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ---------------------------------------------------------------------------
// Analytics — fire-and-forget writes, never throw
// ---------------------------------------------------------------------------
async function logProductView(productId, businessId) {
  if (!productId || !businessId) return;
  try {
    await addDoc(collection(db, 'productViews'), {
      productId,
      businessId,
      uid: auth.currentUser?.uid || null,
      timestamp: serverTimestamp(),
    });
  } catch (_) { /* silent */ }
}

async function logAffiliateClick(productId, businessId) {
  if (!productId || !businessId) return;
  try {
    await addDoc(collection(db, 'affiliateClicks'), {
      productId,
      businessId,
      uid: auth.currentUser?.uid || null,
      timestamp: serverTimestamp(),
    });
  } catch (_) { /* silent */ }
}

// ---------------------------------------------------------------------------
// NutritionTile — compact grid cell
// ---------------------------------------------------------------------------
const NutritionTile = ({ label, value, unit }) => {
  if (value === null || value === undefined) return null;
  const display = typeof value === 'number'
    ? (Number.isInteger(value) ? value : Number(value).toFixed(1))
    : value;
  return (
    <div className="flex flex-col items-center justify-center py-md px-xs bg-surface rounded-large text-center min-w-0 overflow-hidden">
      <span className="text-title-3 font-bold text-text-primary leading-none w-full text-center truncate">
        {display}
      </span>
      {unit && (
        <span className="text-[10px] font-normal text-text-tertiary leading-none mt-xs">{unit}</span>
      )}
      <span className="text-[10px] text-text-tertiary mt-xs leading-tight w-full text-center truncate">{label}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// StarRating — interactive input
// ---------------------------------------------------------------------------
const StarRating = ({ value, onChange, readonly = false, size = 18 }) => {
  const [hovered, setHovered] = useState(null);
  const display = hovered ?? value;
  return (
    <div className="flex items-center gap-xs">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(n)}
          onMouseEnter={() => !readonly && setHovered(n)}
          onMouseLeave={() => !readonly && setHovered(null)}
          className={readonly ? 'cursor-default' : 'cursor-pointer'}
        >
          <Star
            size={size}
            className={n <= display ? 'fill-warning text-warning' : 'text-text-tertiary/30'}
          />
        </button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ReviewCard
// ---------------------------------------------------------------------------
const ReviewCard = ({ review, onReport }) => (
  <div
    className="p-lg rounded-large bg-surface"
    style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
  >
    <div className="flex items-start justify-between gap-md mb-sm">
      <div>
        <p className="text-callout font-semibold text-text-primary">{review.displayName || 'Anonymous'}</p>
        <p className="text-footnote text-text-tertiary">{formatDate(review.createdAt)}</p>
      </div>
      <div className="flex items-center gap-sm flex-shrink-0">
        <StarRating value={review.rating} readonly size={13} />
        <button
          onClick={() => onReport(review)}
          className="text-text-tertiary hover:text-error transition-colors"
          title="Report review"
        >
          <Flag size={13} />
        </button>
      </div>
    </div>
    {review.comment && (
      <p className="text-body text-text-secondary leading-relaxed">{review.comment}</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// ReportReviewModal
// ---------------------------------------------------------------------------
const ReportReviewModal = ({ review, productId, onClose }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!reason) return;
    if (reason === 'Other' && !details.trim()) return;
    try {
      setSubmitting(true);
      await addDoc(collection(db, 'reviewReports'), {
        productId,
        reviewId: review.id,
        reportedBy: auth.currentUser.uid,
        reason,
        details: details.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting report:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-md"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
    >
      <div
        className="bg-background rounded-large w-full overflow-hidden"
        style={{ maxWidth: '440px' }}
      >
        <div className="flex items-center justify-between p-lg border-b border-text-tertiary/10">
          <div className="flex items-center gap-sm">
            <Flag size={16} className="text-error" />
            <h3 className="text-callout font-semibold text-text-primary">Report Review</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface transition-all"
          >
            <X size={16} className="text-text-tertiary" />
          </button>
        </div>

        {submitted ? (
          <div className="p-xl text-center">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-md">
              <Check size={22} className="text-success" />
            </div>
            <p className="text-callout font-semibold text-text-primary mb-sm">Report submitted</p>
            <p className="text-body text-text-secondary mb-lg">
              Our team will review this report and take action if needed.
            </p>
            <button
              onClick={onClose}
              className="px-lg py-sm bg-primary text-white rounded-large text-callout font-semibold hover:bg-primary/90 transition-all"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-lg space-y-md">
            <div>
              <p className="text-caption font-semibold text-text-tertiary uppercase tracking-wide mb-sm">
                Reason
              </p>
              <div className="space-y-xs">
                {REPORT_REASONS.map(r => (
                  <button
                    key={r}
                    onClick={() => setReason(r)}
                    className={`w-full text-left px-md py-sm rounded-large border transition-all text-body ${
                      reason === r
                        ? 'border-primary bg-primary/5 text-primary font-semibold'
                        : 'border-text-tertiary/15 text-text-secondary hover:border-primary/40'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-caption font-semibold text-text-tertiary uppercase tracking-wide mb-sm">
                Additional details {reason !== 'Other' && <span className="normal-case font-normal">(optional)</span>}
                {reason === 'Other' && <span className="normal-case font-normal text-error"> (required)</span>}
              </p>
              <textarea
                value={details}
                onChange={e => setDetails(e.target.value)}
                placeholder="Provide any additional context..."
                rows={3}
                className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-large text-body focus:border-primary focus:outline-none resize-none"
              />
            </div>

            <div className="flex gap-sm pt-sm">
              <button
                onClick={onClose}
                className="flex-1 py-sm border border-text-tertiary/20 rounded-large text-callout font-semibold text-text-secondary hover:bg-surface transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!reason || (reason === 'Other' && !details.trim()) || submitting}
                className="flex-1 py-sm bg-error text-white rounded-large text-callout font-semibold hover:bg-error/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ProductDetailView
// ---------------------------------------------------------------------------
const ProductDetailView = ({ product, onClose, onBack }) => {
  const closeHandler = onClose || onBack;
  const navigate = useNavigate();
  const { groceryLists, addItemToList } = useGroceryLists();
  const { addItem: addGuestItem } = useGuestList();
  const { stores } = useStores();

  const [quantity, setQuantity] = useState(1);
  const [imageError, setImageError] = useState(false);
  const [addedToList, setAddedToList] = useState(null);
  const [showListPicker, setShowListPicker] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savingFavorite, setSavingFavorite] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  // Report state
  const [reportTarget, setReportTarget] = useState(null);

  const overlayRef = useRef(null);
  const isGuest = !auth.currentUser;

  // ── Lock body scroll & ESC key ──────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') closeHandler?.(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [closeHandler]);

  // ── Log product view ────────────────────────────────────────────────────
  useEffect(() => {
    if (product?.id && product?.businessId) {
      logProductView(product.id, product.businessId);
    }
  }, [product?.id, product?.businessId]);

  // ── Check saved status ──────────────────────────────────────────────────
  useEffect(() => {
    if (!auth.currentUser || !product?.id) return;
    getDoc(doc(db, 'products', product.id)).then(snap => {
      if (snap.exists()) {
        setIsSaved((snap.data().savedBy || []).includes(auth.currentUser.uid));
      }
    }).catch(() => {});
  }, [product?.id]);

  // ── Fetch reviews ───────────────────────────────────────────────────────
  const fetchReviews = useCallback(async () => {
    if (!product?.id) return;
    try {
      setReviewsLoading(true);
      const q = query(
        collection(db, 'products', product.id, 'reviews'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setReviews(fetched);
      if (auth.currentUser) {
        setHasReviewed(fetched.some(r => r.uid === auth.currentUser.uid));
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  }, [product?.id]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  if (!product) return null;

  // ── Derived product fields ──────────────────────────────────────────────
  const imageUrl = product.imageUrl || product.imageURL || product.image;
  const showImage = imageUrl && !imageError;

  const nutrition = product.nutrition || {};
  const calories = nutrition.calories ?? product.calories;
  const protein  = nutrition.protein  ?? product.protein;
  const carbs    = nutrition.carbs    ?? product.carbs;
  const fat      = nutrition.fat      ?? product.fat;
  const fiber    = nutrition.fiber    ?? product.fiber;
  const sugar    = nutrition.sugar    ?? product.sugar;
  const sodium   = nutrition.sodium   ?? product.sodium;
  const hasNutrition = [calories, protein, carbs, fat].some(v => v !== null && v !== undefined);

  const rawIngredients = product.ingredients || [];
  const ingredients = rawIngredients.map(i => ({
    name: safeString(i.name || i),
    allergens: Array.isArray(i.allergens) ? i.allergens : [],
  }));
  const hasAllergens = ingredients.some(i => i.allergens.length > 0);

  const storeIds = product.storeReports ? Object.keys(product.storeReports) : [];
  const productStores = storeIds.map(id => stores.find(s => s.id === id)).filter(Boolean);

  const averageRating = product.averageRating || product.rating || 0;
  const reviewCount = reviews.length || product.reviewCount || 0;

  const getCategoryIcon = (category) => {
    const props = { size: 80, className: 'text-primary/20' };
    switch (category?.toLowerCase()) {
      case 'supplements': return <Pill {...props} />;
      case 'ingredients': return <Apple {...props} />;
      case 'snacks':      return <Cookie {...props} />;
      case 'beverages':   return <ShoppingBag {...props} />;
      default:            return <Package {...props} />;
    }
  };

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleAddToList = async (listId) => {
    const item = {
      name: product.name,
      quantity,
      category: product.category || 'other',
      isChecked: false,
      sourceProductId: product.id,
      productImageURL: imageUrl || null,
    };
    try {
      if (isGuest) {
        addGuestItem({ ...item, id: `guest_${Date.now()}` });
        setAddedToList('guest');
      } else {
        await addItemToList(listId, item);
        setAddedToList(listId);
      }
      setShowListPicker(false);
      setTimeout(() => setAddedToList(null), 2000);
    } catch (err) {
      console.error('Error adding to list:', err);
    }
  };

  const handleAddClick = () => {
    if (isGuest) { handleAddToList(null); return; }
    if (groceryLists.length === 0) return;
    if (groceryLists.length === 1) { handleAddToList(groceryLists[0].id); return; }
    setShowListPicker(true);
  };

  const handleToggleSave = async () => {
    if (!auth.currentUser) return;
    try {
      setSavingFavorite(true);
      const { arrayUnion, arrayRemove, updateDoc: uDoc, doc: fDoc } = await import('firebase/firestore');
      const ref = fDoc(db, 'products', product.id);
      if (isSaved) {
        await uDoc(ref, { savedBy: arrayRemove(auth.currentUser.uid) });
        setIsSaved(false);
      } else {
        await uDoc(ref, { savedBy: arrayUnion(auth.currentUser.uid) });
        setIsSaved(true);
      }
    } catch (err) {
      console.error('Error toggling save:', err);
    } finally {
      setSavingFavorite(false);
    }
  };

  const handleAffiliateClick = () => {
    logAffiliateClick(product.id, product.businessId);
    window.open(product.affiliateUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSubmitReview = async () => {
    setReviewError('');
    if (reviewRating === 0) { setReviewError('Please select a rating.'); return; }
    if (!reviewComment.trim()) { setReviewError('Please write a comment.'); return; }
    try {
      setSubmittingReview(true);
      const user = auth.currentUser;
      await addDoc(collection(db, 'products', product.id, 'reviews'), {
        uid: user.uid,
        displayName: user.displayName || 'Anonymous',
        rating: reviewRating,
        comment: reviewComment.trim(),
        createdAt: serverTimestamp(),
      });
      // Update denormalized stats on the product document
      const productRef = doc(db, 'products', product.id);
      const newCount = reviewCount + 1;
      const newAvg = ((averageRating * reviewCount) + reviewRating) / newCount;
      await updateDoc(productRef, {
        reviewCount: newCount,
        averageRating: parseFloat(newAvg.toFixed(2)),
        updatedAt: serverTimestamp(),
      });
      setReviewSuccess(true);
      setShowReviewForm(false);
      setReviewRating(0);
      setReviewComment('');
      fetchReviews();
    } catch (err) {
      console.error('Error submitting review:', err);
      setReviewError('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-50 flex items-center justify-center p-md"
        style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
        onClick={(e) => { if (e.target === overlayRef.current) closeHandler?.(); }}
      >
        <div
          className="bg-background w-full rounded-large overflow-hidden flex flex-col relative"
          style={{ maxWidth: '920px', maxHeight: '92vh' }}
        >

          {/* ── HEADER: image + core info ─────────────────────────────── */}
          <div className="flex flex-col sm:flex-row flex-shrink-0">

            {/* Image */}
            <div
              className="bg-gradient-to-br from-accent/10 to-primary/5 flex items-center justify-center flex-shrink-0"
              style={{ width: '100%', maxWidth: '300px', minHeight: '260px' }}
            >
              {showImage ? (
                <img
                  src={imageUrl}
                  alt={product.name}
                  className="object-contain p-xl"
                  style={{ maxHeight: '260px', maxWidth: '100%' }}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="flex items-center justify-center p-xl">
                  {getCategoryIcon(product.category)}
                </div>
              )}
            </div>

            {/* Core info */}
            <div className="flex-1 px-xl pt-xl pb-lg flex flex-col justify-between min-w-0">
              <div>
                {/* Close */}
                <div className="flex justify-end mb-sm">
                  <button
                    onClick={closeHandler}
                    className="w-8 h-8 bg-surface rounded-full flex items-center justify-center hover:bg-primary/10 transition-all"
                  >
                    <X size={16} className="text-text-primary" />
                  </button>
                </div>

                {/* Brand + badges */}
                <div className="flex items-center gap-sm flex-wrap mb-xs">
                  {product.brand && (
                    product.businessId ? (
                      <button
                        onClick={() => { closeHandler?.(); navigate(`/brands/${product.businessId}`); }}
                        className="text-caption font-semibold text-primary uppercase tracking-wide hover:underline transition-all"
                      >
                        {product.brand}
                      </button>
                    ) : (
                      <span className="text-caption font-semibold text-primary uppercase tracking-wide">
                        {product.brand}
                      </span>
                    )
                  )}
                  {product.isVerified && (
                    <span className="flex items-center gap-xs px-sm py-xs bg-success/10 text-success text-footnote font-semibold rounded-small">
                      <ShieldCheck size={11} /> Verified
                    </span>
                  )}
                  {product.category && (
                    <span className="px-sm py-xs bg-accent/10 text-accent text-footnote font-semibold rounded-small">
                      {product.category}
                    </span>
                  )}
                </div>

                {/* Name */}
                <h2 className="text-title-2 font-rounded text-primary mb-sm leading-tight">
                  {product.name}
                </h2>

                {/* Rating summary */}
                {averageRating > 0 && (
                  <div className="flex items-center gap-sm mb-sm">
                    <StarRating value={Math.round(averageRating)} readonly size={14} />
                    <span className="text-callout font-semibold text-text-primary">
                      {averageRating.toFixed(1)}
                    </span>
                    <span className="text-callout text-text-tertiary">
                      ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                    </span>
                  </div>
                )}

                {/* Dietary tags */}
                {product.dietaryTags?.length > 0 && (
                  <div className="flex flex-wrap gap-xs mb-sm">
                    {product.dietaryTags.map((tag, i) => (
                      <span key={i} className="px-sm py-xs bg-accent/10 text-accent text-caption rounded-small">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Store availability */}
                {productStores.length > 0 && (
                  <div className="flex items-center gap-xs flex-wrap">
                    <Store size={12} className="text-text-tertiary" />
                    {productStores.map((store, i) => (
                      <span key={i} className="text-caption text-text-secondary">
                        {store.name}{i < productStores.length - 1 ? ' ·' : ''}
                      </span>
                    ))}
                  </div>
                )}

                {/* Description */}
                {product.description && (
                  <p className="text-body text-text-secondary leading-relaxed mt-sm line-clamp-3">
                    {product.description}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="mt-md space-y-sm">
                {/* Affiliate link */}
                {product.affiliateUrl && (
                  <button
                    onClick={handleAffiliateClick}
                    className="w-full flex items-center justify-center gap-sm py-sm rounded-large bg-primary text-white text-callout font-semibold hover:bg-primary/90 active:scale-95 transition-all"
                  >
                    <ExternalLink size={15} />
                    Shop Now
                  </button>
                )}

                {/* Quantity + Add to List + Save */}
                <div className="flex items-center gap-sm">
                  <div className="flex items-center border border-text-tertiary/20 rounded-large overflow-hidden flex-shrink-0">
                    <button
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="w-9 h-9 flex items-center justify-center hover:bg-primary/10 transition-all"
                    >
                      <Minus size={13} className="text-primary" />
                    </button>
                    <span className="w-8 text-center text-body font-semibold text-text-primary">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(q => q + 1)}
                      className="w-9 h-9 flex items-center justify-center hover:bg-primary/10 transition-all"
                    >
                      <Plus size={13} className="text-primary" />
                    </button>
                  </div>

                  <button
                    onClick={handleAddClick}
                    disabled={product.inStock === false}
                    className={`flex-1 flex items-center justify-center gap-sm py-sm rounded-large text-callout font-semibold transition-all ${
                      addedToList
                        ? 'bg-success text-white'
                        : product.inStock === false
                        ? 'bg-text-tertiary/20 text-text-tertiary cursor-not-allowed'
                        : 'bg-primary/10 text-primary hover:bg-primary/20 active:scale-95'
                    }`}
                  >
                    {addedToList
                      ? <><Check size={15} /> Added</>
                      : <><ShoppingBag size={15} /> Add to List</>
                    }
                  </button>

                  <button
                    onClick={handleToggleSave}
                    disabled={savingFavorite || isGuest}
                    className={`w-9 h-9 flex items-center justify-center border rounded-large transition-all flex-shrink-0 ${
                      isSaved
                        ? 'bg-error border-error text-white'
                        : 'border-text-tertiary/20 text-text-tertiary hover:text-error hover:border-error'
                    } disabled:opacity-40`}
                  >
                    <Heart size={16} className={isSaved ? 'fill-current' : ''} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── SCROLLABLE BODY ───────────────────────────────────────── */}
          <div className="overflow-y-auto flex-1 border-t border-text-tertiary/10">

            {/* ── Nutrition Information ── */}
            {hasNutrition && (
              <div className="px-xl py-lg border-b border-text-tertiary/10">
                <h3 className="text-callout font-semibold text-text-primary mb-xs">
                  Nutrition Information
                </h3>
                {product.servingSize && (
                  <p className="text-footnote text-text-tertiary mb-md">
                    Per serving ({product.servingSize})
                  </p>
                )}
                <div className="grid grid-cols-4 md:grid-cols-7 gap-sm">
                  <NutritionTile label="Calories" value={calories} unit="" />
                  <NutritionTile label="Fat"      value={fat}      unit="g" />
                  <NutritionTile label="Carbs"    value={carbs}    unit="g" />
                  <NutritionTile label="Protein"  value={protein}  unit="g" />
                  <NutritionTile label="Fiber"    value={fiber}    unit="g" />
                  <NutritionTile label="Sugar"    value={sugar}    unit="g" />
                  <NutritionTile label="Sodium"   value={sodium}   unit="mg" />
                </div>
              </div>
            )}

            {/* ── Ingredients ── */}
            {ingredients.length > 0 && (
              <div className="px-xl py-lg border-b border-text-tertiary/10">
                <h3 className="text-callout font-semibold text-text-primary mb-sm">
                  Ingredients ({ingredients.length})
                </h3>
                <div className="flex flex-wrap gap-xs">
                  {ingredients.map((ingredient, i) => {
                    const hasAllergen = ingredient.allergens.length > 0;
                    return (
                      <span
                        key={i}
                        className={`px-sm py-xs text-caption rounded-small ${
                          hasAllergen
                            ? 'bg-error/10 text-error border border-error/20'
                            : 'bg-surface border border-text-tertiary/15 text-text-secondary'
                        }`}
                        title={hasAllergen ? `Allergens: ${ingredient.allergens.join(', ')}` : ''}
                      >
                        {ingredient.name}
                      </span>
                    );
                  })}
                </div>
                {hasAllergens && (
                  <div className="flex items-center gap-xs mt-sm">
                    <AlertTriangle size={12} className="text-error flex-shrink-0" />
                    <p className="text-footnote text-error">Red items contain allergens</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Product Details ── */}
            {(product.size || product.servingSize || product.barcode || product.certifications?.length > 0) && (
              <div className="px-xl py-lg border-b border-text-tertiary/10">
                <h3 className="text-callout font-semibold text-text-primary mb-md">Product Details</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-md">
                  {product.size && (
                    <div>
                      <p className="text-footnote text-text-tertiary mb-xs">Size</p>
                      <p className="text-body text-text-primary">{product.size}</p>
                    </div>
                  )}
                  {product.servingSize && (
                    <div>
                      <p className="text-footnote text-text-tertiary mb-xs">Serving Size</p>
                      <p className="text-body text-text-primary">{product.servingSize}</p>
                    </div>
                  )}
                  {product.barcode && (
                    <div>
                      <p className="text-footnote text-text-tertiary mb-xs">Barcode</p>
                      <p className="text-body text-text-primary font-mono">{product.barcode}</p>
                    </div>
                  )}
                </div>
                {product.certifications?.length > 0 && (
                  <div className="flex flex-wrap gap-sm mt-md">
                    {product.certifications.map((cert, i) => (
                      <span key={i} className="px-md py-xs bg-success/10 text-success text-caption rounded-small font-semibold">
                        {cert}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Reviews ── */}
            <div className="px-xl py-lg">
              <div className="flex items-center justify-between mb-lg">
                <div>
                  <h3 className="text-callout font-semibold text-text-primary">
                    Reviews {reviewCount > 0 && `(${reviewCount})`}
                  </h3>
                  {averageRating > 0 && (
                    <div className="flex items-center gap-sm mt-xs">
                      <StarRating value={Math.round(averageRating)} readonly size={13} />
                      <span className="text-body font-semibold text-text-primary">
                        {averageRating.toFixed(1)} out of 5
                      </span>
                    </div>
                  )}
                </div>
                {!isGuest && !hasReviewed && !showReviewForm && (
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="flex items-center gap-xs px-md py-sm bg-primary/10 text-primary rounded-large text-callout font-semibold hover:bg-primary/20 transition-all"
                  >
                    <Star size={14} />
                    Write a Review
                  </button>
                )}
                {reviewSuccess && (
                  <span className="flex items-center gap-xs text-success text-callout font-semibold">
                    <Check size={14} /> Review submitted
                  </span>
                )}
              </div>

              {/* Review form */}
              {showReviewForm && (
                <div
                  className="p-lg rounded-large mb-lg"
                  style={{ border: '1px solid rgba(var(--color-text-tertiary),0.15)', background: 'var(--color-surface)' }}
                >
                  <h4 className="text-callout font-semibold text-text-primary mb-md">Your Review</h4>

                  <div className="mb-md">
                    <p className="text-caption text-text-tertiary mb-sm">Rating</p>
                    <StarRating value={reviewRating} onChange={setReviewRating} size={22} />
                  </div>

                  <div className="mb-md">
                    <p className="text-caption text-text-tertiary mb-sm">Comment</p>
                    <textarea
                      value={reviewComment}
                      onChange={e => setReviewComment(e.target.value)}
                      placeholder="Share your experience with this product..."
                      rows={4}
                      maxLength={2000}
                      className="w-full px-md py-sm bg-background border border-text-tertiary/20 rounded-large text-body focus:border-primary focus:outline-none resize-none"
                    />
                    <p className="text-footnote text-text-tertiary text-right mt-xs">
                      {reviewComment.length}/2000
                    </p>
                  </div>

                  {reviewError && (
                    <p className="text-caption text-error mb-md">{reviewError}</p>
                  )}

                  <div className="flex gap-sm">
                    <button
                      onClick={() => { setShowReviewForm(false); setReviewError(''); }}
                      className="flex-1 py-sm border border-text-tertiary/20 rounded-large text-callout font-semibold text-text-secondary hover:bg-background transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitReview}
                      disabled={submittingReview}
                      className="flex-1 py-sm bg-primary text-white rounded-large text-callout font-semibold hover:bg-primary/90 transition-all disabled:opacity-40"
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </button>
                  </div>
                </div>
              )}

              {/* Review list */}
              {reviewsLoading ? (
                <div className="flex justify-center py-xl">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : reviews.length === 0 ? (
                <div
                  className="text-center py-xl rounded-large"
                  style={{ border: '1px dashed rgba(var(--color-text-tertiary),0.2)' }}
                >
                  <Star size={28} className="text-text-tertiary/40 mx-auto mb-sm" />
                  <p className="text-body text-text-tertiary">No reviews yet</p>
                  {!isGuest && !hasReviewed && (
                    <p className="text-caption text-text-tertiary mt-xs">Be the first to review this product</p>
                  )}
                </div>
              ) : (
                <div className="space-y-md">
                  {reviews.map(review => (
                    <ReviewCard
                      key={review.id}
                      review={review}
                      onReport={setReportTarget}
                    />
                  ))}
                </div>
              )}

              {/* Guest prompt for reviews */}
              {isGuest && (
                <p className="text-caption text-text-tertiary text-center mt-md">
                  Sign in to write a review
                </p>
              )}
            </div>

            <div className="h-4" />
          </div>

          {/* ── List picker sheet ── */}
          {showListPicker && (
            <div
              className="absolute inset-0 z-10 flex flex-col justify-end rounded-large overflow-hidden"
              style={{ background: 'rgba(0,0,0,0.4)' }}
            >
              <div className="bg-background p-xl">
                <div className="flex items-center justify-between mb-lg">
                  <h3 className="text-title-3 font-rounded text-primary">Add to which list?</h3>
                  <button
                    onClick={() => setShowListPicker(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface"
                  >
                    <X size={16} className="text-text-tertiary" />
                  </button>
                </div>
                <div className="space-y-sm max-h-[260px] overflow-y-auto">
                  {groceryLists.map(list => (
                    <button
                      key={list.id}
                      onClick={() => handleAddToList(list.id)}
                      className="w-full flex items-center justify-between px-lg py-md rounded-large border border-text-tertiary/15 hover:border-primary hover:bg-primary/5 transition-all text-left"
                    >
                      <span className="text-body font-semibold text-text-primary">
                        {safeString(list.name)}
                      </span>
                      <span className="text-caption text-text-tertiary">
                        {list.items?.length || 0} items
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Report modal — rendered outside main modal stack ── */}
      {reportTarget && (
        <ReportReviewModal
          review={reportTarget}
          productId={product.id}
          onClose={() => setReportTarget(null)}
        />
      )}
    </>
  );
};

export default ProductDetailView;